import { baseApi } from "../../../app/api";
import type { ThemeVariables } from "../../../types";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";

// Import Types for this API slice
import type {
  TrCityWithDistricts,
  TrCity,
  PlaceOrderParams,
  RawPlaceOrderResponse,
  TransformedPlaceOrderResult,
  InitiatePaytrPaymentParams,
  InitiatePaytrPaymentResult,
  InitiatePluxeePaymentParams,
  InitiatePluxeePaymentResult,
} from "../types/checkoutApiTypes";

// Import helper functions
import {
  transformAddressToRaw,
  transformAddress,
} from "../../cart/api/cartApi";

// --- Base URL Constants ---
const WC_STORE_API_BASE =
  window.themeVariables?.wcStoreApiBase || "/wp-json/wc/store/v1";

// --- Static Data Path Helper ---
const getStaticDataPath = (): string => {
  const themeVars = window.themeVariables as ThemeVariables | undefined;
  const themePath = themeVars?.themePath || "";
  const isDevelopment = import.meta.env.DEV;
  const devDataPathBase = `${themePath}/assets/src/data`;
  const prodDataPathBase = `${themePath}/assets/dist/assets/data`;
  return isDevelopment ? devDataPathBase : prodDataPathBase;
};

// --- Transformation Function (for placeOrder) ---
export function transformPlaceOrderResponse(
  response: RawPlaceOrderResponse
): TransformedPlaceOrderResult {
  return {
    orderId: response.order_id,
    status: response.status,
    orderKey: response.order_key,
    customerNote: response.customer_note,
    customerId: response.customer_id,
    billingAddress: transformAddress(response.billing_address),
    shippingAddress: transformAddress(response.shipping_address),
    paymentMethod: response.payment_method,
    paymentResult: response.payment_result
      ? {
          paymentStatus: response.payment_result.payment_status,
          paymentDetails: response.payment_result.payment_details || [],
          redirectUrl: response.payment_result.redirect_url || "",
        }
      : { paymentStatus: "error", paymentDetails: [], redirectUrl: "" },
  };
}

// --- API Slice Definition ---
export const checkoutApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // --- Static Data Endpoints ---
    getCities: builder.query<TrCity[], void>({
      async queryFn(_arg, _queryApi, _extraOptions) {
        const dataPathBase = getStaticDataPath();
        try {
          const response = await fetch(`${dataPathBase}/tr_cities.json`);
          if (!response.ok)
            throw new Error(`Failed to fetch cities: ${response.statusText}`);
          const data: TrCity[] = await response.json();
          return { data };
        } catch (error: unknown) {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to load city data";
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: errorMessage,
            } as FetchBaseQueryError,
          };
        }
      },
      providesTags: ["Cities"],
    }),
    getCitiesWithDistricts: builder.query<TrCityWithDistricts[], void>({
      async queryFn(_arg, _queryApi, _extraOptions) {
        const dataPathBase = getStaticDataPath();
        try {
          const response = await fetch(
            `${dataPathBase}/tr_cities_districts.json`
          );
          if (!response.ok)
            throw new Error(
              `Failed to fetch districts: ${response.statusText}`
            );
          const data: TrCityWithDistricts[] = await response.json();
          return { data };
        } catch (error: unknown) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to load district data";
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: errorMessage,
            } as FetchBaseQueryError,
          };
        }
      },
      providesTags: ["CitiesWithDistricts"],
    }),

    // --- Place Order Mutation (for BACS/COD) ---
    placeOrder: builder.mutation<TransformedPlaceOrderResult, PlaceOrderParams>(
      {
        query: (params) => {
          const body: Record<string, any> = {
            billing_address: transformAddressToRaw(params.billingAddress),
            shipping_address: transformAddressToRaw(params.shippingAddress),
            payment_method: params.paymentMethod,
            ...(params.customerNote && { customer_note: params.customerNote }),
          };
          return {
            url: `${WC_STORE_API_BASE}/checkout`,
            method: "POST",
            body: body,
          };
        },
        transformResponse: transformPlaceOrderResponse,
        invalidatesTags: ["Cart"],
      }
    ),

    /**
     * Initiates the PayTR payment process by calling the custom plugin endpoint.
     */
    initiatePaytrPayment: builder.mutation<
      InitiatePaytrPaymentResult,
      InitiatePaytrPaymentParams
    >({
      query: (params) => {
        const body = {
          cart: params.cart,
          // Since the addresses are already inside the cart object from the frontend state,
          // we don't need to explicitly add them again here if they are part of params.cart.
          // The PHP controller is set up to expect a 'cart' object containing them.
        };

        // Note: The `X-WP-Nonce` header is automatically added by the `baseApi`'s
        // `prepareHeaders` function from `themeVariables`.

        return {
          // Construct the RELATIVE path from the site root.
          // The baseApi adds the `http://localhost:8000` part.
          url: `/wp-json/birikiuc-paytr/v1/paytr/initiate-payment`,
          method: "POST",
          body: body,
        };
      },
      // No transformResponse needed if the raw response matches our desired type
    }),
    /**
     * Initiates the Pluxee OTP payment process.
     */
    initiatePluxeePayment: builder.mutation<
      InitiatePluxeePaymentResult,
      InitiatePluxeePaymentParams
    >({
      query: (params) => ({
        // The Pluxee plugin endpoint
        url: `/wp-json/birikiuc-pluxee/v1/pluxee/initiate-payment`,
        method: "POST",
        body: params, // Body includes { cart, gsm, otp }
      }),
      // No transformResponse needed if the raw response matches our desired type
    }),
  }),
  overrideExisting: false,
});

// --- Export Hooks and Endpoints ---
export const {
  useGetCitiesQuery,
  useGetCitiesWithDistrictsQuery,
  usePlaceOrderMutation,
  useInitiatePaytrPaymentMutation,
  useInitiatePluxeePaymentMutation,
} = checkoutApi;

export const {
  getCities,
  getCitiesWithDistricts,
  placeOrder,
  initiatePaytrPayment,
  initiatePluxeePayment,
} = checkoutApi.endpoints;
